import { useEffect, useMemo, useState, useDeferredValue } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Package, Search, Building2, Plus, Edit2, Trash2 } from "lucide-react";
import ProductModal from "../components/ProductModal";
import ProductImage from "../components/ProductImage";
import { useAuth } from "../contexts/AuthContext";
import { BrandFormModal } from "../components/admin/BrandsTab";
import { confirmDialog } from "../utils/confirmDialog";
import { smartMatch } from "../utils/arabicSearch";
import { inStockFirst } from "../utils/stockSort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CategoryDetail = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // ALL products across categories — used to show a selected brand's items from other categories
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null); // null = brand view, "all" = all products
  const [searchQ, setSearchQ] = useState("");
  const [productSearchQ, setProductSearchQ] = useState("");
  // Deferred queries let React keep the input field responsive while the
  // filter runs in the background — makes typing feel instant even on
  // large product lists.
  const deferredSearchQ = useDeferredValue(searchQ);
  const deferredProductSearchQ = useDeferredValue(productSearchQ);

  // Admin brand-management state
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);

  const fetchData = async () => {
    try {
      const [catsRes, prodsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`),
      ]);
      setCategories(catsRes.data || []);
      const cat = catsRes.data.find((c) => c.id === categoryId);
      setCategory(cat);
      setAllProducts(prodsRes.data || []);
      if (cat) {
        const brandsEndpoint = isAdmin ? `${API}/admin/brands` : `${API}/brands`;
        const { data: brs } = await axios.get(brandsEndpoint, { params: { category: cat.name } });
        setBrands(brs || []);
        setProducts(prodsRes.data.filter((p) => p.category === cat.name));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [categoryId, isAdmin]); // eslint-disable-line

  const filteredBrands = useMemo(() => {
    if (!deferredSearchQ.trim()) return brands;
    return brands.filter((b) => smartMatch(b.name_ar || "", deferredSearchQ));
  }, [brands, deferredSearchQ]);

  const visibleProducts = useMemo(() => {
    if (!selectedBrandId) return [];
    const base = selectedBrandId === "all" ? products : products.filter((p) => p.brand_id === selectedBrandId);
    if (!deferredProductSearchQ.trim()) return inStockFirst(base);
    return inStockFirst(base.filter((p) =>
      smartMatch(`${p.name_ar || ""} ${p.barcode || ""}`, deferredProductSearchQ)
    ));
  }, [products, selectedBrandId, deferredProductSearchQ]);

  // When a single brand is selected, also collect that brand's products from
  // OTHER categories so the customer sees the brand's full catalog on one
  // page: primary (current) category at top, then a section per other
  // category in which the brand has products. This avoids customers having
  // to click in and out of categories to assemble a full brand order.
  const brandOtherCategoryGroups = useMemo(() => {
    if (!selectedBrandId || selectedBrandId === "all" || !category) return [];
    const others = allProducts.filter(
      (p) => p.brand_id === selectedBrandId && p.category && p.category !== category.name,
    );
    if (!others.length) return [];
    // Preserve admin category order
    const groups = new Map();
    others.forEach((p) => {
      if (!groups.has(p.category)) groups.set(p.category, []);
      groups.get(p.category).push(p);
    });
    const orderedNames = categories.map((c) => c.name).filter((n) => groups.has(n));
    // Also include any orphan category names not in the admin list (defensive)
    groups.forEach((_, name) => {
      if (!orderedNames.includes(name)) orderedNames.push(name);
    });
    return orderedNames.map((name) => {
      const meta = categories.find((c) => c.name === name);
      return { name, id: meta?.id || name, color: meta?.color, items: inStockFirst(groups.get(name)) };
    });
  }, [selectedBrandId, allProducts, category, categories]);

  const showingProducts = selectedBrandId !== null;
  const currentBrandName = selectedBrandId && selectedBrandId !== "all"
    ? brands.find((b) => b.id === selectedBrandId)?.name_ar
    : "كل المنتجات";

  const handleDeleteBrand = async (e, brand) => {
    e.stopPropagation();
    if (!(await confirmDialog(`حذف شركة "${brand.name_ar}"؟\n\nالمنتجات (${brand.product_count}) لن تُحذف — ستبقى مرئية ضمن "كل المنتجات".`))) return;
    try {
      const { data } = await axios.delete(`${API}/admin/brands/${brand.id}`);
      toast.success(`تم حذف الشركة (${data.products_detached} منتج مفصول)`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  const handleEditBrand = (e, brand) => {
    e.stopPropagation();
    setEditingBrand(brand);
    setShowBrandForm(true);
  };

  // Single product card — extracted so the primary grid and per-category
  // sections (below) render identical cards without duplicating JSX.
  const renderProductCard = (product, index) => {
    const outOfStock = (product.stock ?? 0) <= 0;
    return (
      <motion.div
        key={product.id}
        data-testid={`category-product-${product.id}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index, 12) * 0.04 }}
        whileHover={!outOfStock ? { y: -4 } : {}}
        onClick={() => !outOfStock && setSelectedProduct(product)}
        className={`bg-[#E0F2FE] border border-[#BAE6FD] rounded-2xl overflow-hidden transition-all relative ${
          outOfStock ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:shadow-lg"
        }`}
      >
        {outOfStock && (
          <div className="absolute inset-0 bg-black/40 z-30 flex items-center justify-center">
            <span className="bg-[#E85D45] text-white font-bold px-4 py-2 rounded-full text-sm">غير متوفر</span>
          </div>
        )}
        <div
          className="aspect-square flex items-center justify-center p-6 relative overflow-hidden"
          style={{ backgroundColor: product.color }}
        >
          {(product.image_path || product.image_path2) ? (
            <ProductImage image={product.image_path} image2={product.image_path2} alt={product.name_ar} className="w-full h-full" />
          ) : (
            <motion.h3 className="font-heading font-bold text-xl text-center text-[#111814]"
              animate={{ scale: [1, 1.03, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              {product.name_ar}
            </motion.h3>
          )}
        </div>
        <div className="p-4 space-y-3 bg-[#E0F2FE]">
          <p className="text-sm text-[#5C6E64]">{product.name_ar}</p>
          <div className="flex items-center justify-between">
            <span className="font-bold text-lg text-[#2D4A3E]" dir="ltr">
              {Number(product.price).toLocaleString("en-US", { maximumFractionDigits: 0 })} د.ع
            </span>
            <span className="bg-[#E85D45] text-white px-5 py-2 rounded-full text-sm font-medium">اشتري</span>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-[#E4EBE6]">
        <div className="max-w-md mx-auto md:max-w-3xl lg:max-w-5xl px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => {
                if (showingProducts) { setSelectedBrandId(null); }
                else navigate(-1);
              }}
              data-testid="category-back"
              className="p-2 hover:bg-[#F3F6F4] rounded-full transition-colors"
            >
              <ArrowRight className="w-5 h-5 text-[#2D4A3E]" />
            </button>
            <h1 className="font-heading font-bold text-xl sm:text-2xl text-[#2D4A3E] flex-1 truncate">
              {loading ? "..." : showingProducts ? `${category?.name_ar} — ${currentBrandName}` : category?.name_ar || "قسم"}
            </h1>
            {isAdmin && !showingProducts && (
              <button
                onClick={() => { setEditingBrand(null); setShowBrandForm(true); }}
                data-testid="add-brand-btn"
                className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold px-3 py-2 rounded-full flex items-center gap-1 text-sm shrink-0"
              >
                <Plus className="w-4 h-4" /> إضافة شركة
              </button>
            )}
          </div>

          {!showingProducts && (
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="ابحث عن شركة..."
                data-testid="brand-search"
                className="w-full pr-9 pl-4 py-2 rounded-full bg-[#F3F6F4] border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              />
            </div>
          )}

          {showingProducts && (
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
              <input
                type="text"
                value={productSearchQ}
                onChange={(e) => setProductSearchQ(e.target.value)}
                placeholder="ابحث عن منتج بالاسم أو الباركود..."
                data-testid="product-search"
                className="w-full pr-9 pl-4 py-2 rounded-full bg-[#F3F6F4] border border-[#E4EBE6] text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
              />
              {productSearchQ && (
                <button
                  onClick={() => setProductSearchQ("")}
                  data-testid="product-search-clear"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6E64] hover:text-[#E85D45]"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-md mx-auto md:max-w-3xl lg:max-w-5xl px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square bg-[#BAE6FD]/40 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : showingProducts ? (
          // ===== Products view (brand selected) =====
          (visibleProducts.length === 0 && brandOtherCategoryGroups.length === 0) ? (
            <div className="text-center py-16">
              <Package className="w-20 h-20 text-[#BAE6FD] mx-auto mb-4" />
              <p className="text-lg text-[#5C6E64]">لا توجد منتجات لهذه الشركة بعد</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Primary section — products in the category the customer
                  entered from. Shown FIRST because the entry category is
                  the customer's main intent. Skip header for "all" view. */}
              {visibleProducts.length > 0 && (
                <section>
                  {selectedBrandId !== "all" && category && (
                    <div
                      data-testid="brand-section-primary"
                      className="flex items-center gap-2 mb-3 bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl px-4 py-2 shadow"
                    >
                      <span className="bg-white/25 rounded-full px-2 py-0.5 text-[10px] font-bold">القسم الأساسي</span>
                      <h3 className="font-heading font-bold text-base">
                        {category.name} — {currentBrandName} <span className="opacity-80 text-xs">({visibleProducts.length})</span>
                      </h3>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    {visibleProducts.map((product, index) =>
                      renderProductCard(product, index)
                    )}
                  </div>
                </section>
              )}

              {/* Other categories — same brand, different sections. Lets
                  customers complete the brand's order without bouncing
                  between category pages. */}
              {brandOtherCategoryGroups.map((g) => (
                <section key={g.id} data-testid={`brand-section-${g.id}`}>
                  <button
                    type="button"
                    onClick={() => g.id && navigate(`/category/${g.id}`)}
                    className="w-full flex items-center justify-between gap-2 mb-3 bg-white border-2 border-[#3B82F6]/40 text-[#1E3A5F] rounded-2xl px-4 py-2 shadow hover:bg-[#E0F2FE] transition"
                    title={`الذهاب إلى قسم ${g.name}`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{ backgroundColor: g.color || "#3B82F6" }}
                      />
                      <h3 className="font-heading font-bold text-base">
                        قسم {g.name} <span className="opacity-70 text-xs">({g.items.length})</span>
                      </h3>
                    </div>
                    <span className="text-xs text-[#3B82F6] font-bold">عرض القسم ←</span>
                  </button>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    {g.items.map((product, index) =>
                      renderProductCard(product, index)
                    )}
                  </div>
                </section>
              ))}
            </div>
          )
        ) : (
          // ===== Brands view (default) =====
          <>
            {filteredBrands.length === 0 ? (
              <div className="text-center py-16">
                <Building2 className="w-20 h-20 text-[#BAE6FD] mx-auto mb-4" />
                <p className="text-lg text-[#5C6E64]">
                  {searchQ ? "لا توجد شركة بهذا الاسم" : "لا توجد شركات في هذا القسم بعد"}
                </p>
                {isAdmin && !searchQ && (
                  <button
                    onClick={() => { setEditingBrand(null); setShowBrandForm(true); }}
                    data-testid="empty-add-brand-btn"
                    className="mt-4 bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold px-5 py-2 rounded-full inline-flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> أضف أول شركة
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-[#5C6E64] mb-3">
                  اختر الشركة لعرض منتجاتها ({filteredBrands.length} شركة)
                </p>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  {filteredBrands.map((brand, index) => (
                    <motion.div
                      key={brand.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -4 }}
                      onClick={() => setSelectedBrandId(brand.id)}
                      data-testid={`brand-tile-${brand.id}`}
                      className="group bg-white border-2 border-[#BAE6FD] rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all relative"
                    >
                      {isAdmin && (
                        <div className="absolute top-2 left-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => handleEditBrand(e, brand)}
                            data-testid={`edit-brand-${brand.id}`}
                            title="تعديل"
                            className="bg-[#3B82F6] hover:bg-[#2563eb] text-white p-1.5 rounded-full shadow-md"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteBrand(e, brand)}
                            data-testid={`delete-brand-${brand.id}`}
                            title="حذف"
                            className="bg-[#E85D45] hover:bg-[#c14a36] text-white p-1.5 rounded-full shadow-md"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <div className="aspect-square flex items-center justify-center p-6 bg-gradient-to-br from-[#F0F9FF] to-[#E0F2FE]">
                        {brand.image_path ? (
                          <img src={`${API}/files/${brand.image_path}`} alt={brand.name_ar} className="w-full h-full object-contain" />
                        ) : (
                          <Building2 className="w-16 h-16 text-[#3B82F6]" />
                        )}
                      </div>
                      <div className="p-3 bg-white text-center border-t border-[#E4EBE6]">
                        <p className="font-bold text-[#1E3A5F]">{brand.name_ar}</p>
                        <p className="text-xs text-[#5C6E64] mt-1" dir="ltr">{brand.product_count} منتج</p>
                      </div>
                    </motion.div>
                  ))}

                  {/* "كل المنتجات" tile */}
                  <motion.div
                    whileHover={{ y: -4 }}
                    onClick={() => setSelectedBrandId("all")}
                    data-testid="all-products-tile"
                    className="bg-gradient-to-br from-[#1E3A5F] to-[#152a45] text-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all"
                  >
                    <div className="aspect-square flex flex-col items-center justify-center p-6">
                      <Package className="w-16 h-16 opacity-80 mb-2" />
                      <p className="font-bold text-lg">كل المنتجات</p>
                      <p className="text-xs opacity-80 mt-1" dir="ltr">{products.length} منتج</p>
                    </div>
                  </motion.div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      <ProductModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
      />

      {/* Admin: Brand Create/Edit Modal */}
      {showBrandForm && (
        <BrandFormModal
          brand={editingBrand ? { ...editingBrand, category: category?.name || editingBrand.category } : null}
          categories={category ? [category] : categories}
          onClose={() => { setShowBrandForm(false); setEditingBrand(null); }}
          onSaved={fetchData}
        />
      )}
    </div>
  );
};

export default CategoryDetail;
